// src/quiz/Who5Test.js
//
// The World Health Organization-Five Well-Being Index (WHO-5).
// Source: World Health Organization. The World Health Organization-Five
// Well-Being Index (WHO-5). Geneva: World Health Organization; 2024.
// WHO/UCN/MSD/MHE/2024.1. License: CC BY-NC-SA 3.0 IGO.
// Items, response scale and scoring are reproduced unchanged from the WHO
// publication. WHO does not endorse PsyX; the WHO logo is not used.

import React, { useState, useEffect } from "react";
import getApiBaseUrl from "../utils/api";
import { useNavigate } from "react-router-dom";

const TEST_NAME = "WHO-5 Well-Being Index";

const QUESTIONS = [
  "I have felt cheerful and in good spirits",
  "I have felt calm and relaxed",
  "I have felt active and vigorous",
  "I woke up feeling fresh and rested",
  "My daily life has been filled with things that interest me"
];

const OPTIONS = [
  { label: "All of the time", value: 5 },
  { label: "Most of the time", value: 4 },
  { label: "More than half of the time", value: 3 },
  { label: "Less than half of the time", value: 2 },
  { label: "Some of the time", value: 1 },
  { label: "At no time", value: 0 }
];

// Raw score below 13 (percentage below 50) is the suggested cut-off for
// poor mental well-being (Topp et al., 2015).
const CUTOFF_RAW = 13;

export const WHO5_SOURCE = {
  citation:
    "World Health Organization. The World Health Organization-Five Well-Being Index (WHO-5). Geneva: World Health Organization; 2024. WHO/UCN/MSD/MHE/2024.1. License: CC BY-NC-SA 3.0 IGO.",
  url: "https://www.who.int/health-topics/mental-health",
  licenseUrl: "https://creativecommons.org/licenses/by-nc-sa/3.0/igo",
  reference:
    "Topp CW, Østergaard SD, Søndergaard S, Bech P. The WHO-5 Well-Being Index: a systematic review of the literature. Psychother Psychosom. 2015;84(3):167–176. doi:10.1159/000376585."
};

export function Who5Credits() {
  return (
    <div style={styles.credits}>
      <h3 style={styles.creditsTitle}>Source & Credits</h3>
      <p>
        © World Health Organization 2024. The WHO-5 was developed by the late
        Per Bech (Psychiatric Centre North Zealand, Denmark). Copyright was
        assigned to WHO in 2024.
      </p>
      <p>
        <strong>Citation:</strong> {WHO5_SOURCE.citation}{" "}
        <a href={WHO5_SOURCE.url} target="_blank" rel="noopener noreferrer" style={styles.link}>
          WHO Department of Mental Health, Brain Health and Substance Use
        </a>
      </p>
      <p>
        <strong>Scoring cut-off reference:</strong> {WHO5_SOURCE.reference}
      </p>
      <p>
        Used under the{" "}
        <a href={WHO5_SOURCE.licenseUrl} target="_blank" rel="noopener noreferrer" style={styles.link}>
          Creative Commons Attribution-NonCommercial-ShareAlike 3.0 IGO licence
        </a>
        . WHO does not endorse PsyX or any specific organization, product or service.
      </p>
    </div>
  );
}

export const getWho5Level = (rawScore) =>
  rawScore < CUTOFF_RAW ? "Poor Mental Well-Being" : "Good Mental Well-Being";

export default function Who5Test() {
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [resultData, setResultData] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const API_URL = getApiBaseUrl();
  const navigate = useNavigate();

  const handleChange = (index, value) => {
    const updated = [...answers];
    updated[index] = value;
    setAnswers(updated);
  };

  const calculateScore = async () => {
    if (answers.includes(null)) {
      alert("Please answer all questions before submitting");
      return;
    }

    // Raw score 0–25, percentage score = raw × 4 (0–100)
    const rawScore = answers.reduce((sum, value) => sum + value, 0);
    const percentageScore = rawScore * 4;
    const level = getWho5Level(rawScore);
    const result = { rawScore, percentageScore, level };

    try {
      const response = await fetch(`${API_URL}/tests/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ testName: TEST_NAME, result })
      });

      const savedData = await response.json();
      setResultData(savedData.result || result);
    } catch (error) {
      console.error("Error saving WHO-5 result:", error);
      setResultData(result);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= RESULT SCREEN ================= */

  if (resultData) {
    const isLow = resultData.rawScore < CUTOFF_RAW;

    return (
      <div style={styles.container}>
        <h1 style={styles.heading}>{TEST_NAME}</h1>

        <div style={styles.resultCard}>
          <h2>Your Well-Being Result</h2>

          <div style={styles.scoreText}>
            Well-Being Score: {resultData.percentageScore}%
          </div>
          <div style={styles.subScore}>Raw score: {resultData.rawScore} / 25</div>

          <div style={styles.levelText}>{resultData.level}</div>

          <p style={styles.interpretation}>
            {isLow
              ? "Your score is below 50%, the suggested cut-off for poor mental well-being. This is not a diagnosis, but it is a sign that a further check-in with a doctor, counsellor or mental health professional could be helpful."
              : "Your score is 50% or above, which suggests your mental well-being over the last two weeks has been in a healthy range. Keep looking after the habits that support you."}
          </p>

          <p style={styles.disclaimer}>
            The WHO-5 is a short screening questionnaire, not a diagnostic test.
            If you are struggling or feel unsafe, please reach out to a
            healthcare professional or your local emergency services.
          </p>

          <div style={styles.savedMessage}>✅ Result saved to your profile!</div>

          <button onClick={() => navigate("/profile")} style={styles.button}>
            View Profile
          </button>
        </div>

        <Who5Credits />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>The World Health Organization-Five Well-Being Index (WHO-5)</h1>

      <p style={styles.intro}>
        The WHO-5 is a short questionnaire from the World Health Organization that measures your current mental well-being.
        <br />
        It has just 5 statements and takes about 1–2 minutes — answer based on how you have really been feeling.
      </p>

      <p style={styles.description}>
        Please indicate for each of the five statements which is closest to how
        you have been feeling <strong>over the last two weeks</strong>. Notice
        that higher numbers mean better well-being.
        <br />
        <br />
        <em>
          Example: If you have felt cheerful and in good spirits more than half
          of the time during the last two weeks, select number three.
        </em>
      </p>

      {QUESTIONS.map((question, index) => (
        <div key={index} style={styles.questionCard}>
          <p style={styles.questionText}>
            {index + 1}. {question}
          </p>

          {OPTIONS.map((option) => (
            <label key={option.value} style={styles.optionLabel}>
              <input
                type="radio"
                name={`q${index}`}
                value={option.value}
                checked={answers[index] === option.value}
                onChange={() => handleChange(index, option.value)}
              />{" "}
              <strong>{option.value}</strong> — {option.label}
            </label>
          ))}
        </div>
      ))}

      <button style={styles.button} onClick={calculateScore}>
        Submit
      </button>

      <Who5Credits />
    </div>
  );
}

/* ================= STYLES ================= */

const styles = {
  container: {
    maxWidth: "800px",
    margin: "120px auto 60px",
    padding: "0 20px",
    textAlign: "center"
  },
  heading: {
    fontSize: "30px",
    color: "#19fd91",
    marginBottom: "20px"
  },
  intro: {
    color: "#19fd91",
    fontSize: "1rem",
    marginBottom: "15px",
    lineHeight: "1.6"
  },
  description: {
    color: "#ccc",
    fontSize: "0.95rem",
    marginBottom: "30px",
    lineHeight: "1.6"
  },
  questionCard: {
    background: "#111",
    border: "1px solid #19fd91",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px",
    textAlign: "left"
  },
  questionText: {
    fontWeight: "600"
  },
  optionLabel: {
    display: "block",
    marginTop: "8px",
    cursor: "pointer"
  },
  button: {
    padding: "12px 25px",
    background: "#19fd91",
    color: "#000",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "16px",
    marginTop: "20px"
  },
  resultCard: {
    background: "#111",
    border: "1px solid #19fd91",
    borderRadius: "12px",
    padding: "30px",
    marginTop: "20px"
  },
  scoreText: {
    fontSize: "1.3rem",
    fontWeight: "600",
    color: "#19fd91",
    marginTop: "10px"
  },
  subScore: {
    color: "#ccc",
    marginTop: "4px"
  },
  levelText: {
    fontSize: "1.1rem",
    marginTop: "10px"
  },
  interpretation: {
    color: "#ddd",
    lineHeight: "1.6",
    marginTop: "15px"
  },
  disclaimer: {
    color: "#aaa",
    fontSize: "0.85rem",
    fontStyle: "italic",
    marginTop: "10px"
  },
  savedMessage: {
    marginTop: "15px",
    color: "#19fd91"
  },
  credits: {
    marginTop: "40px",
    padding: "20px",
    borderTop: "1px solid #233127",
    color: "#999",
    fontSize: "0.8rem",
    lineHeight: "1.6",
    textAlign: "left"
  },
  creditsTitle: {
    color: "#19fd91",
    fontSize: "1rem",
    marginBottom: "8px"
  },
  link: {
    color: "#19fd91"
  }
};
